import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readAttachmentContent } from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';
import { REVENUES_KEY } from './RevenuesScreen.js';

function useTenant() {
  return useSessionContext().data?.currentTenant.id;
}

/** A receita aberta: os dados, as cobranças, a origem e o Conta Azul. */
export function useRevenue(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...REVENUES_KEY, tenant, 'detalhe', id],
    queryFn: async () =>
      ok(
        await api.revenues.getRevenue({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'a receita'),
      ),
    enabled: tenant !== undefined,
  });
}

export function useRevenueActivity(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...REVENUES_KEY, tenant, 'atividade', id],
    queryFn: async () =>
      ok(
        await api.revenues.revenueActivity({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'a atividade'),
      ),
    enabled: tenant !== undefined,
  });
}

export function useRevenueFiles(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...REVENUES_KEY, tenant, 'anexos', id],
    queryFn: async () =>
      ok(
        await api.revenues.listRevenueFiles({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'os anexos'),
      ),
    enabled: tenant !== undefined,
  });
}

export function useRevenueLabelOptions(enabled: boolean) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...REVENUES_KEY, tenant, 'etiquetas'],
    enabled,
    queryFn: async () =>
      ok(
        await api.revenues.revenueLabelOptions(),
        200,
        loadFailureMessage(undefined, 'as etiquetas'),
      ),
  });
}

export function useRevenueAssigneeOptions(enabled: boolean) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...REVENUES_KEY, tenant, 'responsaveis'],
    enabled,
    queryFn: async () =>
      ok(
        await api.revenues.revenueAssigneeOptions(),
        200,
        loadFailureMessage(undefined, 'quem pode ser o responsável'),
      ),
  });
}

/** Tudo que grava na mesa da receita — e recarrega o quadro e a receita aberta. */
export function useRevenueDeskMutations(id: number) {
  const client = useQueryClient();
  const onSettled = () => client.invalidateQueries({ queryKey: REVENUES_KEY });
  return {
    setLabels: useMutation({
      mutationFn: async (labelIds: number[]) =>
        ok(
          await api.revenues.setRevenueLabels({ params: { id }, body: { labelIds } }),
          200,
          'Não foi possível gravar as etiquetas.',
        ),
      onSettled,
    }),
    setAssignee: useMutation({
      mutationFn: async (userId: string | null) =>
        ok(
          await api.revenues.setRevenueAssignee({ params: { id }, body: { userId } }),
          200,
          'Não foi possível trocar o responsável.',
        ),
      onSettled,
    }),
    comment: useMutation({
      mutationFn: async (body: string) =>
        ok(
          await api.revenues.commentRevenue({ params: { id }, body: { body } }),
          201,
          'Não foi possível comentar.',
        ),
      onSettled,
    }),
    updateComment: useMutation({
      mutationFn: async ({
        commentId,
        change,
      }: {
        commentId: number;
        change: { body: string } | { archived: true };
      }) =>
        ok(
          await api.revenues.updateRevenueComment({
            params: { id, commentId },
            body: change,
          }),
          200,
          'Não foi possível mudar o comentário.',
        ),
      onSettled,
    }),
    attach: useMutation({
      mutationFn: async (file: File) =>
        ok(
          await api.revenues.attachRevenueFile({
            params: { id },
            body: {
              fileName: file.name,
              mimeType: file.type || 'application/octet-stream',
              content: await readAttachmentContent(file),
            },
          }),
          201,
          `Não foi possível enviar ${file.name}.`,
        ),
      onSettled,
    }),
    archiveFile: useMutation({
      mutationFn: async (fileId: number) =>
        ok(
          await api.revenues.archiveRevenueFile({ params: { id, fileId }, body: {} }),
          200,
          'Não foi possível arquivar o anexo.',
        ),
      onSettled,
    }),
    charge: useMutation({
      mutationFn: async () =>
        ok(
          await api.revenues.chargeRevenue({ params: { id }, body: {} }),
          200,
          'Não foi possível emitir a cobrança.',
        ),
      onSettled,
    }),
    syncContaAzul: useMutation({
      mutationFn: async () =>
        ok(
          await api.revenues.syncRevenueContaAzul({ params: { id }, body: {} }),
          200,
          'Não foi possível falar com o Conta Azul.',
        ),
      onSettled,
    }),
  };
}

export type RevenueDeskMutations = ReturnType<typeof useRevenueDeskMutations>;

/** A URL assinada de curta duração de um arquivo da receita — a prévia dos anexos. */
export async function revenueFileUrl(id: number, key: string): Promise<string> {
  return ok(
    await api.revenues.revenueFileUrl({ params: { id, key } }),
    200,
    'Não foi possível abrir o arquivo.',
  ).url;
}
